/**
 * ReauthModal — full-page password (+ MFA) prompt that gates a route
 * the admin has marked as reauth-required for the current user's role.
 *
 * Activated by `useReauthGate`. Hides the underlying page until the
 * user successfully POSTs `/auth/reauthenticate`. On success, sets a
 * `last_reauth_at` ISO timestamp and `reauth_ttl_seconds` in
 * sessionStorage. Both keys are read by `useReauthGate` to skip the
 * prompt on other reauth-protected pages within the validity window.
 *
 * sessionStorage (NOT localStorage) is intentional: the gate is a
 * UX-only convenience, not a security boundary. Real authorization
 * still lives on the backend per-endpoint. Closing the tab forces a
 * fresh reauth on next visit, which matches the spec: tab-close
 * logout (iter148) wipes localStorage too, so this is moot in
 * practice — but keeps the gate honest if the JWT outlives the tab.
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Lock, Shield, AlertCircle, Loader2 } from 'lucide-react';

export const REAUTH_AT_KEY = 'orb_reauth_at';
export const REAUTH_TTL_KEY = 'orb_reauth_ttl';

const ReauthModal = ({ open, pageLabel, onSuccess, onCancel }) => {
  const { user, api } = useAuth();
  const requiresMfa = !!user?.totp_enabled;
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      setPassword('');
      setTotpCode('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const submit = async () => {
    if (!password || (requiresMfa && (!totpCode || totpCode.length < 6))) {
      setError('Enter your password' + (requiresMfa ? ' and 6-digit code.' : '.'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = { password };
      if (requiresMfa) payload.totp_code = totpCode;
      const res = await api.post('/auth/reauthenticate', payload);
      const issuedAt = res.data?.issued_at || new Date().toISOString();
      const ttl = res.data?.valid_duration_seconds ?? 900;
      try {
        sessionStorage.setItem(REAUTH_AT_KEY, issuedAt);
        sessionStorage.setItem(REAUTH_TTL_KEY, String(ttl));
      } catch (_) { /* ignore */ }
      onSuccess?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Reauthentication failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel?.(); }}>
      <DialogContent
        className="bg-white dark:bg-[#1f1f1f] rounded-2xl max-w-md"
        data-testid="reauth-modal"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#586D52]" />
          Confirm it's you
        </DialogTitle>
        <p className="text-sm text-muted-foreground -mt-1">
          {pageLabel ? `${pageLabel} requires reauthentication.` : 'This page requires reauthentication.'}
          {' '}Re-enter your credentials to continue.
        </p>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs flex items-center gap-1"><Lock className="w-3 h-3" /> Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              autoComplete="current-password"
              data-testid="reauth-password"
              onKeyDown={(e) => { if (e.key === 'Enter' && !requiresMfa) submit(); }}
            />
          </div>
          {requiresMfa && (
            <div>
              <Label className="text-xs">Authenticator code</Label>
              <Input
                inputMode="numeric"
                maxLength={6}
                placeholder="123456"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                data-testid="reauth-totp"
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
              />
            </div>
          )}
          {error && (
            <p className="text-xs text-red-600 flex items-center gap-1" data-testid="reauth-error">
              <AlertCircle className="w-3 h-3" /> {error}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="outline"
              onClick={() => onCancel?.()}
              disabled={submitting}
              data-testid="reauth-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={submitting}
              className="bg-[#586D52] hover:bg-[#475a43]"
              data-testid="reauth-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Confirm
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReauthModal;
